<template>
  <div class="contact-container">
    <div class="contact-header">
      <h1>Hubungi Kami</h1>
      <p>Silakan isi formulir di bawah ini untuk menghubungi tim kami. Kami akan merespons pertanyaan Anda sesegera mungkin.</p>
    </div>

    <div class="contact-form-container">
      <form @submit.prevent="submitForm" class="contact-form">
        <div class="form-group">
          <label for="name">Nama <span class="required">*</span></label>
          <input 
            type="text" 
            id="name" 
            v-model="formData.name" 
            class="form-control" 
            :class="{ 'error': validationErrors.name }"
            required
          />
          <span v-if="validationErrors.name" class="error-message">{{ validationErrors.name }}</span>
        </div>

        <div class="form-group">
          <label for="email">Email <span class="required">*</span></label>
          <input 
            type="email" 
            id="email" 
            v-model="formData.email" 
            class="form-control" 
            :class="{ 'error': validationErrors.email }"
            required
          />
          <span v-if="validationErrors.email" class="error-message">{{ validationErrors.email }}</span>
        </div>

        <div class="form-group">
          <label for="phone">Nomor Telepon</label>
          <input 
            type="tel" 
            id="phone" 
            v-model="formData.phone" 
            class="form-control"
            :class="{ 'error': validationErrors.phone }"
          />
          <span v-if="validationErrors.phone" class="error-message">{{ validationErrors.phone }}</span>
        </div>

        <div class="form-group">
          <label for="purpose">Keperluan <span class="required">*</span></label>
          <select 
            id="purpose" 
            v-model="formData.purpose" 
            class="form-control" 
            :class="{ 'error': validationErrors.purpose }"
            required
          >
            <option value="" disabled>Pilih keperluan</option>
            <option value="Pertanyaan Umum">Pertanyaan Umum</option>
            <option value="Dukungan Teknis">Dukungan Teknis</option>
            <option value="Kerjasama">Kerjasama</option>
            <option value="Saran dan Masukan">Saran dan Masukan</option>
            <option value="Lainnya">Lainnya</option>
          </select>
          <span v-if="validationErrors.purpose" class="error-message">{{ validationErrors.purpose }}</span>
        </div>

        <div class="form-group">
          <label for="message">Pesan</label>
          <textarea 
            id="message" 
            v-model="formData.message" 
            class="form-control" 
            rows="5"
            :class="{ 'error': validationErrors.message }"
          ></textarea>
          <span v-if="validationErrors.message" class="error-message">{{ validationErrors.message }}</span>
        </div>

        <div class="form-actions">
          <button type="submit" class="btn btn-primary" :disabled="isSubmitting">
            <font-awesome-icon v-if="isSubmitting" :icon="['fas', 'spinner']" class="fa-spin" />
            {{ isSubmitting ? 'Mengirim...' : 'Kirim Pesan' }}
          </button>
        </div>
      </form>
    </div>

    <!-- Success Modal -->
    <div v-if="showSuccessModal" class="modal-overlay">
      <div class="modal-content">
        <div class="modal-header">
          <h3><font-awesome-icon :icon="['fas', 'check-circle']" class="text-success" /> Pesan Terkirim</h3>
        </div>
        <div class="modal-body">
          <p>Terima kasih telah menghubungi kami. Pesan Anda telah berhasil terkirim. Kami akan segera meninjau dan merespons pesan Anda.</p>
        </div>
        <div class="modal-footer">
          <button @click="closeSuccessModal" class="btn btn-primary">Tutup</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { useHead } from '@unhead/vue';

export default {
  name: 'ContactUs',
  components: {
    FontAwesomeIcon
  },
  setup() {
    // Set meta tags with @unhead/vue
    useHead({
      title: 'Hubungi Kami - HobiBaca',
      meta: [
        { name: 'description', content: 'Hubungi tim HobiBaca untuk pertanyaan, dukungan, kerjasama, dan saran.' },
        { name: 'keywords', content: 'HobiBaca, hubungi kami, dukungan, kerjasama, saran' },
        { property: 'og:title', content: 'Hubungi Kami - HobiBaca' },
        { property: 'og:description', content: 'Hubungi tim HobiBaca untuk pertanyaan, dukungan, kerjasama, dan saran.' },
        { property: 'og:type', content: 'website' },
        { name: 'twitter:title', content: 'Hubungi Kami - HobiBaca' },
        { name: 'twitter:description', content: 'Hubungi tim HobiBaca untuk pertanyaan, dukungan, kerjasama, dan saran.' }
      ]
    });
  },
  data() {
    return {
      formData: {
        name: '',
        email: '',
        phone: '',
        purpose: '',
        message: ''
      },
      validationErrors: {},
      isSubmitting: false,
      showSuccessModal: false
    };
  },
  methods: {
    validateForm() {
      this.validationErrors = {};
      let isValid = true;

      if (!this.formData.name.trim()) {
        this.validationErrors.name = 'Nama harus diisi';
        isValid = false;
      }

      if (!this.formData.email.trim()) {
        this.validationErrors.email = 'Email harus diisi';
        isValid = false;
      } else if (!this.isValidEmail(this.formData.email)) {
        this.validationErrors.email = 'Format email tidak valid';
        isValid = false;
      }

      if (!this.formData.purpose) {
        this.validationErrors.purpose = 'Keperluan harus dipilih';
        isValid = false;
      }

      if (this.formData.phone && !this.isValidPhone(this.formData.phone)) {
        this.validationErrors.phone = 'Format nomor telepon tidak valid';
        isValid = false;
      }

      return isValid;
    },
    
    isValidEmail(email) {
      const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      return emailPattern.test(email);
    },
    
    isValidPhone(phone) {
      // Format nomor telepon: minimal 8 digit, maksimal 15 digit
      const phonePattern = /^[0-9]{8,15}$/;
      return phonePattern.test(phone.replace(/\D/g, ''));
    },
    
    async submitForm() {
      if (!this.validateForm()) {
        return;
      }
      
      this.isSubmitting = true;
      
      try {
        const response = await axios.post(
          `${process.env.VUE_APP_API_BASE_URL}/api/feedback/`, 
          this.formData
        );
        
        if (response.status === 201) {
          // Reset form
          this.formData = {
            name: '',
            email: '',
            phone: '',
            purpose: '',
            message: ''
          };
          
          // Show success modal
          this.showSuccessModal = true;
        }
      } catch (error) {
        console.error('Error submitting form:', error);
        
        // Handle validation errors from backend
        if (error.response && error.response.data) {
          this.validationErrors = error.response.data;
        } else {
          alert('Terjadi kesalahan saat mengirim pesan. Silakan coba lagi nanti.');
        }
      } finally {
        this.isSubmitting = false;
      }
    },
    
    closeSuccessModal() {
      this.showSuccessModal = false;
    }
  }
};
</script>

<style scoped>
.contact-container {
  max-width: 800px;
  margin: 40px auto;
  padding: 0 20px;
}

.contact-header {
  margin-bottom: 30px;
  text-align: center;
}

.contact-header h1 {
  margin-bottom: 10px;
  color: #333;
}

.contact-form-container {
  background-color: white;
  border-radius: 8px;
  padding: 30px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
}

.form-group {
  margin-bottom: 20px;
}

label {
  display: block;
  margin-bottom: 5px;
  font-weight: 600;
  color: #333;
}

.required {
  color: #dc3545;
}

.form-control {
  width: 100%;
  padding: 10px;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 16px;
  transition: border-color 0.2s;
}

.form-control:focus {
  border-color: #4285f4;
  outline: none;
}

.form-control.error {
  border-color: #dc3545;
}

.error-message {
  display: block;
  color: #dc3545;
  font-size: 14px;
  margin-top: 5px;
}

.form-actions {
  margin-top: 30px;
  text-align: center;
}

.btn {
  padding: 10px 20px;
  font-size: 16px;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  transition: background-color 0.2s;
}

.btn-primary {
  background-color: #4285f4;
  color: white;
}

.btn-primary:hover {
  background-color: #3367d6;
}

.btn-primary:disabled {
  background-color: #a4c1f7;
  cursor: not-allowed;
}

/* Modal Styles */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
}

.modal-content {
  background-color: white;
  border-radius: 8px;
  width: 90%;
  max-width: 500px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.2);
}

.modal-header {
  padding: 15px 20px;
  border-bottom: 1px solid #ddd;
}

.modal-header h3 {
  margin: 0;
  display: flex;
  align-items: center;
  gap: 10px;
}

.modal-body {
  padding: 20px;
}

.modal-footer {
  padding: 15px 20px;
  border-top: 1px solid #ddd;
  text-align: right;
}

.text-success {
  color: #28a745;
}

@media (max-width: 600px) {
  .contact-form-container {
    padding: 20px;
  }
}
</style>